import dayjs, { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction, createContext } from 'react';
import { CommunityGroupKmlFiles } from '../../api/dtos/dtos';

export enum MapEvent {
    Refresh = 1
}

export interface CustomHomPageRegion {
    title: string;
    fileName: string;
}

export interface IMapEventListener {
    (): Promise<void>;
}

export class MapEvents {
    listeners: Record<MapEvent, IMapEventListener[]> = {
        [MapEvent.Refresh]: []
    };

    constructor() {

    }

    public subscribe = (event: MapEvent, listener: IMapEventListener) => {
        this.listeners[event].push(listener);

        return () => {
            this.listeners[event] = this.listeners[event].filter(x => x !== listener);
        }
    }

    public trigger = async (event: MapEvent) => {
        await this.notify(event);
    }

    private notify = async (event: MapEvent) => {
        for (let listener of this.listeners[event]) {
            await listener();
        }
    }
}

export enum MapFilterPeriodEnum {
    DateRange = 1,
    Year,
    MonthYear,
    LastSevenDays,
    LastThirtyOneDays
}

export class MapFilterSubmitter {
    static PublicData = 1;
    static AllData = 2;
    static MyData = 3;
    static MyGroup = 4;

    static toString(option: number) {
        switch (option) {
            case this.PublicData: return "Public Data";
            case this.AllData: return "All Data";
            case this.MyData: return "My Data";
            case this.MyGroup: return "My Group";
            default: return "";
        }
    }
}

export type MapFilter = {
    reportingPeriod: ReportingPeriodFilter;
    bySubmitter: SubmitterFilter;
    recordType: RecordTypeFilter;
    layers: LayersFilter;
}

export type ReportingPeriodFilter = {
    period: MapFilterPeriodEnum,
    periodLabel?: string,
    fromField: Dayjs | null,
    toField: Dayjs | null,
    yearField: Dayjs | null,
    monthYearField: Dayjs | null
};

export type SubmitterFilter = {
    prevState?: SubmitterFilter; 
    submitter: number;
    submitterLabel?: string;
    displayHeatmap: boolean;
    communityGroupId?: number;
    communityGroupMemberId?: number;
    locatedInRegionIds?: number[];
    dataAccessId?: number;
};

export type RecordTypeFilter = {
    sighting: boolean;
    damage: boolean;
    control: boolean | null;
    disease: boolean | null;
    subSpeciesId?: number;
};

export type LayersFilter = {
    communityGroupKmls: CommunityGroupKmlFiles[];
    selectedCommunityGroupKmls: number[];
    barrierFences: boolean;
    knownRegions: boolean;
    releaseSites: boolean;
    rhdBoostRollOutSites: boolean;
    successfulEoiSites: boolean;
    customHomePageRegions?: CustomHomPageRegion[];
    selectedCustomHomePageRegions?: string[];
}

export type MapCenter = {
    latitude: number,
    longitude: number,
    zoom: number
}

export interface IMapContext {
    drawerOpen: boolean,
    filter: MapFilter,
    setPeriodFilter: Dispatch<SetStateAction<ReportingPeriodFilter>>,
    setSubmitterFilter: Dispatch<SetStateAction<SubmitterFilter>>,
    setRecordTypeFilter: Dispatch<SetStateAction<RecordTypeFilter>>,
    setLayersFilter: Dispatch<SetStateAction<LayersFilter>>,
    openDrawer: () => void,
    closeDrawer: () => void,
    events: MapEvents,
    mapCenter: MapCenter | undefined,
    setMapCenter: Dispatch<SetStateAction<MapCenter | undefined>>,
}

export const GROUPADMIN_MAP_CONTEXT: IMapContext = {
    drawerOpen: false,
    filter: {
        reportingPeriod: {
            period: MapFilterPeriodEnum.DateRange,
            periodLabel: undefined,
            fromField: dayjs().subtract(1, 'year'),
            toField: dayjs(),
            yearField: dayjs(),
            monthYearField: dayjs()
        },
        bySubmitter: {
            prevState: undefined,
            submitter: MapFilterSubmitter.MyGroup,
            submitterLabel: undefined,
            displayHeatmap: false,
            communityGroupId: undefined,
            communityGroupMemberId: undefined,
            locatedInRegionIds: undefined,
            dataAccessId: undefined
        },
        recordType: {
            sighting: true,
            damage: true,
            control: true,
            disease: false,
            subSpeciesId: -1
        },
        layers: {
            communityGroupKmls: [],
            selectedCommunityGroupKmls: [],
            barrierFences: true,
            knownRegions: false,
            releaseSites: false,
            rhdBoostRollOutSites: false,
            successfulEoiSites: false
        }
    },
    setPeriodFilter: () => { },
    setSubmitterFilter: () => { },
    setRecordTypeFilter: () => { },
    setLayersFilter: () => { },
    openDrawer: () => { },
    closeDrawer: () => { },
    mapCenter: undefined,
    setMapCenter: () => { },
    events: new MapEvents()
}

export const AUTHORIZED_MAP_CONTEXT: IMapContext = {
    drawerOpen: false,
    filter: {
        reportingPeriod: {
            period: MapFilterPeriodEnum.DateRange,
            periodLabel: undefined,
            fromField: dayjs().subtract(1, 'year'),
            toField: dayjs(),
            yearField: dayjs(),
            monthYearField: dayjs()
        },
        bySubmitter: {
            prevState: undefined,
            submitter: MapFilterSubmitter.MyData,
            submitterLabel: undefined,
            displayHeatmap: true,
            communityGroupId: undefined,
            communityGroupMemberId: undefined,
            locatedInRegionIds: undefined,
            dataAccessId: undefined
        },
        recordType: {
            sighting: true,
            damage: true,
            control: true,
            disease: false,
            subSpeciesId: -1
        },
        layers: {
            communityGroupKmls: [],
            selectedCommunityGroupKmls: [],
            barrierFences: true,
            knownRegions: false,
            releaseSites: false,
            rhdBoostRollOutSites: false,
            successfulEoiSites: false
        }
    },
    setPeriodFilter: () => { },
    setSubmitterFilter: () => { },
    setRecordTypeFilter: () => { },
    setLayersFilter: () => { },
    openDrawer: () => { },
    closeDrawer: () => { },
    mapCenter: undefined,
    setMapCenter: () => { },
    events: new MapEvents()
}

export const DATAACCESS_MAP_CONTEXT: IMapContext = {
    drawerOpen: false,
    filter: {
        reportingPeriod: {
            period: MapFilterPeriodEnum.DateRange,
            periodLabel: undefined,
            fromField: dayjs().subtract(1, 'year'),
            toField: dayjs(),
            yearField: dayjs(),
            monthYearField: dayjs()
        },
        bySubmitter: {
            prevState: undefined,
            submitter: MapFilterSubmitter.AllData,
            submitterLabel: 'Region Data',
            displayHeatmap: true,
            communityGroupId: undefined,
            communityGroupMemberId: undefined,
            locatedInRegionIds: undefined,
            dataAccessId: undefined
        },
        recordType: {
            sighting: true,
            damage: true,
            control: true,
            disease: false,
            subSpeciesId: -1
        },
        layers: {
            communityGroupKmls: [],
            selectedCommunityGroupKmls: [],
            barrierFences: true,
            knownRegions: false,
            releaseSites: false,
            rhdBoostRollOutSites: false,
            successfulEoiSites: false
        }
    },
    setPeriodFilter: () => { },
    setSubmitterFilter: () => { },
    setRecordTypeFilter: () => { },
    setLayersFilter: () => { },
    openDrawer: () => { },
    closeDrawer: () => { },
    mapCenter: undefined,
    setMapCenter: () => { },
    events: new MapEvents()
}

export const DEFAULT_MAP_CONTEXT: IMapContext = {
    drawerOpen: false,
    filter: {
        reportingPeriod: {
            period: MapFilterPeriodEnum.DateRange,
            periodLabel: undefined,
            fromField: dayjs().subtract(1, 'year'),
            toField: dayjs(),
            yearField: dayjs(),
            monthYearField: dayjs()
        },
        bySubmitter: {
            prevState: undefined,
            submitter: MapFilterSubmitter.PublicData,
            submitterLabel: undefined,
            displayHeatmap: true,
            communityGroupId: undefined,
            communityGroupMemberId: undefined,
            locatedInRegionIds: undefined,
            dataAccessId: undefined
        },
        recordType: {
            sighting: true,
            damage: true,
            control: false,
            disease: false,
            subSpeciesId: -1
        },
        layers: {
            communityGroupKmls: [],
            selectedCommunityGroupKmls: [],
            barrierFences: true,
            knownRegions: false,
            releaseSites: false,
            rhdBoostRollOutSites: false,
            successfulEoiSites: false
        }
    },
    setPeriodFilter: () => { },
    setSubmitterFilter: () => { },
    setRecordTypeFilter: () => { },
    setLayersFilter: () => { },
    openDrawer: () => { },
    closeDrawer: () => { },
    mapCenter: undefined,
    setMapCenter: () => { },
    events: new MapEvents()
}

const MapContext = createContext<IMapContext>(DEFAULT_MAP_CONTEXT);

export default MapContext;